export type Dimension = 'data' | 'skills' | 'useCases' | 'governance' | 'budget'

export interface AssessmentQuestion {
  id: string
  dimension: Dimension
  question: string
  options: { label: string; score: number }[]
}

export const DIMENSION_LABELS: Record<Dimension, string> = {
  data: 'Data foundations',
  skills: 'Skills and literacy',
  useCases: 'Use case clarity',
  governance: 'Governance',
  budget: 'Budget and sponsorship',
}

export const ASSESSMENT_QUESTIONS: AssessmentQuestion[] = [
  {
    id: 'q1',
    dimension: 'data',
    question: 'Can your team agree, without a meeting, what a core record such as a customer or a case actually means?',
    options: [
      { label: 'Definitions are documented and agreed across departments', score: 10 },
      { label: 'Mostly agreed, with some known disputes', score: 6 },
      { label: 'Each department holds its own version', score: 3 },
      { label: 'We have not tried to agree this', score: 0 },
    ],
  },
  {
    id: 'q2',
    dimension: 'data',
    question: 'How quickly can an analyst get access to the data they need?',
    options: [
      { label: 'Same day, through a standing permission', score: 10 },
      { label: 'Within a week, through a request', score: 6 },
      { label: 'Several weeks, with negotiation', score: 3 },
      { label: 'Access is effectively blocked', score: 0 },
    ],
  },
  {
    id: 'q3',
    dimension: 'skills',
    question: 'Can the people who would use an AI output read it critically and challenge it?',
    options: [
      { label: 'Yes, and they regularly do', score: 10 },
      { label: 'Some can, unevenly across teams', score: 6 },
      { label: 'Few can, and they defer to the system', score: 3 },
      { label: 'No, this capability does not exist yet', score: 0 },
    ],
  },
  {
    id: 'q4',
    dimension: 'skills',
    question: 'Has your organisation run structured AI training in the past twelve months?',
    options: [
      { label: 'Yes, role specific and repeated', score: 10 },
      { label: 'Yes, a single general session', score: 5 },
      { label: 'Planned but not delivered', score: 2 },
      { label: 'No training has taken place', score: 0 },
    ],
  },
  {
    id: 'q5',
    dimension: 'useCases',
    question: 'Can you name the figure in your management accounts that a first AI use case would move?',
    options: [
      { label: 'Yes, with a target and a timeframe', score: 10 },
      { label: 'Yes, but without a target', score: 6 },
      { label: 'We have candidate areas, not a figure', score: 3 },
      { label: 'Not yet', score: 0 },
    ],
  },
  {
    id: 'q6',
    dimension: 'useCases',
    question: 'How many AI initiatives are currently open across the organisation?',
    options: [
      { label: 'Two or three, deliberately chosen', score: 10 },
      { label: 'One, as a first trial', score: 7 },
      { label: 'More than five, running in parallel', score: 3 },
      { label: 'None', score: 2 },
    ],
  },
  {
    id: 'q7',
    dimension: 'governance',
    question: 'Is there a written policy covering acceptable AI use and human review?',
    options: [
      { label: 'Yes, approved and in force', score: 10 },
      { label: 'Drafted, not yet approved', score: 5 },
      { label: 'Under discussion', score: 2 },
      { label: 'Nothing written', score: 0 },
    ],
  },
  {
    id: 'q8',
    dimension: 'governance',
    question: 'If an AI assisted decision affected someone unfairly, is there a named route of appeal?',
    options: [
      { label: 'Yes, documented and communicated', score: 10 },
      { label: 'Informally, through a manager', score: 5 },
      { label: 'It has not been considered', score: 0 },
    ],
  },
  {
    id: 'q9',
    dimension: 'governance',
    question: 'Who reviews AI outputs before they inform a decision?',
    options: [
      { label: 'A named reviewer independent of the build team', score: 10 },
      { label: 'The team that built the system', score: 4 },
      { label: 'Nobody at present', score: 0 },
    ],
  },
  {
    id: 'q10',
    dimension: 'budget',
    question: 'Is there an executive sponsor accountable for AI outcomes?',
    options: [
      { label: 'Yes, at board or executive level', score: 10 },
      { label: 'Yes, at head of department level', score: 6 },
      { label: 'Ownership is shared and unclear', score: 2 },
      { label: 'No sponsor', score: 0 },
    ],
  },
  {
    id: 'q11',
    dimension: 'budget',
    question: 'Has the two year running cost, not just the build cost, been budgeted?',
    options: [
      { label: 'Yes, including monitoring and retraining', score: 10 },
      { label: 'Build cost only', score: 4 },
      { label: 'No budget allocated yet', score: 0 },
    ],
  },
  {
    id: 'q12',
    dimension: 'data',
    question: 'Are automated data quality checks in place on the records you would rely on?',
    options: [
      { label: 'Yes, running daily with alerts', score: 10 },
      { label: 'Periodic manual checks', score: 5 },
      { label: 'Issues are found when something breaks', score: 1 },
      { label: 'No checks', score: 0 },
    ],
  },
]

export interface Band {
  min: number
  name: string
  description: string
  nextSteps: { title: string; detail: string; packageName: string }[]
}

export const BANDS: Band[] = [
  {
    min: 75,
    name: 'Ready to build',
    description:
      'Your foundations hold. The risk now is spreading effort across too many initiatives rather than concentrating it on the two that move a number your board watches.',
    nextSteps: [
      { title: 'Build one governed pilot', detail: 'Take your strongest use case through design, build and handover with the review process in place from day one.', packageName: 'AI Pilot Build' },
      { title: 'Instrument the result', detail: 'Put reporting around the pilot so the value is visible weekly, not argued about quarterly.', packageName: 'Demand Engine Build' },
      { title: 'Keep counsel on hand', detail: 'A standing monthly session for your leadership team as adoption widens.', packageName: 'AI Advisory' },
    ],
  },
  {
    min: 50,
    name: 'Ready in parts',
    description:
      'You have real strengths and at least one dimension that will cap the rest. Fix the weakest dimension before you commit build budget, or the pilot will stall for reasons that have nothing to do with the model.',
    nextSteps: [
      { title: 'Close the readiness gap', detail: 'A scored assessment across all five dimensions, with a sequenced plan for the weakest.', packageName: 'AI Readiness' },
      { title: 'Choose two use cases properly', detail: 'Shortlist and score candidate use cases against the numbers your board already watches.', packageName: 'AI Readiness' },
      { title: 'Then build one', detail: 'Move to a single governed pilot once the gap is closed.', packageName: 'AI Pilot Build' },
    ],
  },
  {
    min: 25,
    name: 'Foundations first',
    description:
      'Building now would produce a demonstration rather than a result. The work in front of you is data definitions, ownership and a written policy. It is unglamorous and it carries most of the return.',
    nextSteps: [
      { title: 'Start with readiness', detail: 'An honest scored assessment and an executive briefing that sets expectations across the leadership team.', packageName: 'AI Readiness' },
      { title: 'Agree your data definitions', detail: 'Fix definitions, ownership and quality checks for the twenty fields that matter.', packageName: 'AI Readiness' },
      { title: 'Write the governance', detail: 'Policy, review and appeal, approved before any build begins.', packageName: 'AI Advisory' },
    ],
  },
  {
    min: 0,
    name: 'Early',
    description:
      'There is no adoption gap to close yet, which is an advantage. You can set the foundations correctly rather than unpicking a false start. Begin with sponsorship and a single named use case.',
    nextSteps: [
      { title: 'Secure a sponsor and a use case', detail: 'An executive briefing that establishes accountability and selects one candidate use case.', packageName: 'AI Readiness' },
      { title: 'Understand your market first', detail: 'Where AI is changing buyer behaviour in your sector, and what that means for your plan.', packageName: 'Market Scan' },
      { title: 'Keep counsel on hand', detail: 'Monthly advisory while you build the internal case.', packageName: 'AI Advisory' },
    ],
  },
]

export function scoreAssessment(answers: Record<string, number>): {
  total: number
  band: Band
  byDimension: { dimension: Dimension; score: number; max: number }[]
} {
  const totals: Record<Dimension, { score: number; max: number }> = {
    data: { score: 0, max: 0 },
    skills: { score: 0, max: 0 },
    useCases: { score: 0, max: 0 },
    governance: { score: 0, max: 0 },
    budget: { score: 0, max: 0 },
  }

  ASSESSMENT_QUESTIONS.forEach((question) => {
    const best = Math.max(...question.options.map((option) => option.score))
    totals[question.dimension].max += best
    totals[question.dimension].score += answers[question.id] ?? 0
  })

  const rawScore = Object.values(totals).reduce((sum, entry) => sum + entry.score, 0)
  const rawMax = Object.values(totals).reduce((sum, entry) => sum + entry.max, 0)
  const total = Math.round((rawScore / rawMax) * 100)
  const band = BANDS.find((candidate) => total >= candidate.min) ?? BANDS[BANDS.length - 1]

  return {
    total,
    band,
    byDimension: (Object.keys(totals) as Dimension[]).map((dimension) => ({
      dimension,
      score: totals[dimension].score,
      max: totals[dimension].max,
    })),
  }
}
